import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import debugHelper from '../../admin/utils/debugHelper';

// Phase 1 i18n — translator.trans() 返回 vnode array，flatten 为 string。
function t(key) {
  const result = app.translator.trans(key);
  return Array.isArray(result) ? result.join('') : result;
}
function tRich(key, params) {
  return app.translator.trans(key, params || {});
}

// 装备类型定义 — 跟 SeedreamPage.js EQUIPMENT_TYPES 同源, 保持一致
// v0.4.7 #364 — 装备历史 modal 显示用 (类型 → 中文标签 + 图标)
const EQUIPMENT_TYPES = [{
  key: 'clothing',
  label: () => t('ziven-dress-up.forum.common.equipment_clothing'),
  icon: 'fa-tshirt'
}, {
  key: 'pants',
  label: () => t('ziven-dress-up.forum.common.equipment_pants'),
  icon: 'fa-vest-patches'
}, {
  key: 'helmet',
  label: () => t('ziven-dress-up.forum.common.equipment_helmet'),
  icon: 'fa-hard-hat'
}, {
  key: 'boots',
  label: () => t('ziven-dress-up.forum.common.equipment_boots'),
  icon: 'fa-shoe-prints'
}, {
  key: 'gloves',
  label: () => t('ziven-dress-up.forum.common.equipment_gloves'),
  icon: 'fa-mitten'
}, {
  key: 'goggles',
  label: () => t('ziven-dress-up.forum.common.equipment_goggles'),
  icon: 'fa-glasses'
}, {
  key: 'snowboard',
  label: () => t('ziven-dress-up.forum.common.equipment_snowboard'),
  icon: 'fa-person-skiing'
}, {
  key: 'backpack',
  label: () => t('ziven-dress-up.forum.common.equipment_backpack'),
  icon: 'fa-bag-shopping'
}, {
  key: 'background',
  label: () => t('ziven-dress-up.forum.common.equipment_background'),
  icon: 'fa-image'
}];
const EQUIPMENT_TYPE_MAP = {};
EQUIPMENT_TYPES.forEach(item => {
  EQUIPMENT_TYPE_MAP[item.key] = item;
});

/**
 * v0.4.7 #364 — 装备历史 modal (方案 A3)
 *
 * 设计要点 (设计图 A3):
 *   - 竖排列表, 每行 64px 缩略图 + tags + 时间 + 3 按钮 [使用][★][🗑]
 *   - 缩略图点击 → fancybox 预览 (不触发选中)
 *   - 使用按钮 → modal 关闭 + onSelect({ equipment_url, thumbnail_url, types })
 *   - ★ 收藏 toggle 实时改本地 state + 后端 PUT
 *   - 🗑 删除 → 行内原地变成 [取消][确认删除] (无弹窗)
 *   - 滚动到底自动 loadMore (IntersectionObserver, 触底 50px)
 *
 * Props (attrs):
 *   - onSelect: (equipmentData) => void — 父组件传, 接收选中装备后回调
 */
export default class EquipmentHistoryModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);

    // list state
    this.page = 1;
    this.perPage = 10;
    this.items = [];
    this.total = 0;
    this.totalPages = 1;
    this.loading = false;
    this.hasMore = true;
    this.listError = ''; // v0.4.7 #319 教训 — 错误状态要 UI 可见

    // row state
    this.confirmingId = null; // 当前进入删除确认态的行 id, null = 无确认态
    this.togglingFavoriteId = null; // 防止 ★ toggle 期间重复点击

    // ref
    this.listRef = null;
    this.sentinelRef = null; // IntersectionObserver 哨兵元素

    // 首次加载
    this.loadList(true);
  }
  oncreate(vnode) {
    // v0.4.7a 返工 — 移除之前的 try/catch 兜底
    // 之前的兜底没解决根本问题: 父 view() 被覆盖, .Modal 容器不存在
    // → ModalManager.animateShow 找不到 .Modal → null.addEventListener 抛错
    // 修法: view() 改回 content(), 父类 view() 正常工作, animateShow 正常
    super.oncreate(vnode);

    // 设置滚动监听 (IntersectionObserver 替代 scroll event — 更省 CPU)
    // setTimeout(0) 等下一 tick 再找 DOM, 保证 Modal 已挂载完成
    setTimeout(() => this.setupIntersectionObserver(), 0);
  }
  onupdate(vnode) {
    super.onupdate(vnode);

    // 兜底: loadMore 后 sentinel 重新渲染, 需要重新挂 observer
    // (observer 监听的是旧 sentinel 节点, mithril 重渲时 sentinel 是新 DOM 节点)
    // 简单判断: 如果上次记录的 sentinel 已不在 DOM, 重挂
    if (this.observer && this.observedSentinel && !document.body.contains(this.observedSentinel)) {
      this.setupIntersectionObserver();
    }
  }
  onremove() {
    // 清理 observer
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
      this.observedSentinel = null;
    }
    super.onremove();
  }

  /**
   * 设置 IntersectionObserver 监听哨兵元素
   * 哨兵在 list 底部, 进入 viewport 时触发 loadMore
   */
  setupIntersectionObserver() {
    // 找最新的 sentinel 节点 (loadMore 后 sentinel 是新节点)
    // 注: Modal 的 className 在 body 是 'Modal-body EquipmentHistoryModal-body'
    // className() 返回 'EquipmentHistoryModal Modal--normal' 但实际应用在 Modal 容器上 (不在 body 上)
    // 安全选择器: 用 .equipment-history-sentinel (全局唯一)
    const sentinel = document.querySelector('.equipment-history-sentinel');
    if (!sentinel) return;

    // 已挂载同一 sentinel → 跳过
    if (this.observer && this.observedSentinel === sentinel) return;

    // 重挂
    if (this.observer) {
      this.observer.disconnect();
    }
    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.loading && this.hasMore) {
          this.loadList(false);
        }
      });
    }, {
      root: null,
      // viewport
      rootMargin: '0px 0px 50px 0px',
      // 提前 50px 触发
      threshold: 0
    });
    this.observer.observe(sentinel);
    this.observedSentinel = sentinel;
    debugHelper.log('[EquipmentHistory] IntersectionObserver attached');
  }
  className() {
    return 'EquipmentHistoryModal Modal--normal';
  }
  title() {
    return app.translator.trans('ziven-dress-up.forum.equipment_history.title');
  }

  /**
   * 加载列表 — replace 模式 (首次/刷新) 或 append 模式 (loadMore)
   */
  async loadList(replace) {
    if (replace === void 0) {
      replace = false;
    }
    if (this.loading) return;
    this.loading = true;
    if (replace) {
      this.listError = '';
      this.page = 1; // 重置页码, 防止上次的 page=2 影响
    }
    m.redraw();
    try {
      const response = await app.request({
        method: 'GET',
        url: '/api/ziven/seedream/equipment-history',
        params: {
          page: this.page,
          per_page: this.perPage
        }
      });
      if (response.success && response.data) {
        var _response$data$pagina, _response$data$pagina2;
        const newItems = response.data.items || [];
        if (replace) {
          this.items = newItems;
        } else {
          this.items = this.items.concat(newItems);
        }
        this.total = ((_response$data$pagina = response.data.pagination) == null ? void 0 : _response$data$pagina.total) || 0;
        this.totalPages = ((_response$data$pagina2 = response.data.pagination) == null ? void 0 : _response$data$pagina2.total_pages) || 1;
        // loadList(false) 完成后 page++, 让下次 loadMore 拿下一页
        this.page++;
        this.hasMore = this.page <= this.totalPages;
        debugHelper.log('[EquipmentHistory] Loaded', this.items.length, '/', this.total, 'nextPage=', this.page);
      } else {
        this.listError = response.error || t('ziven-dress-up.forum.equipment_history_modal.load_error');
        debugHelper.warn('[EquipmentHistory] Load failed:', response);
      }
    } catch (error) {
      this.listError = error.message || t('ziven-dress-up.forum.equipment_history_modal.load_error');
      debugHelper.error('[EquipmentHistory] Load error:', error);
    }
    this.loading = false;
    m.redraw();
  }

  /**
   * 切换收藏 (本地立即 toggle, 后端 PUT)
   * 失败回滚 — 友好提示
   */
  async toggleFavorite(item) {
    if (this.togglingFavoriteId === item.id) return; // 防止重复
    this.togglingFavoriteId = item.id;
    const prevValue = item.is_favorite;
    item.is_favorite = !prevValue;
    m.redraw();
    try {
      const response = await app.request({
        method: 'PUT',
        url: "/api/ziven/seedream/equipment-history/".concat(item.id),
        body: {
          is_favorite: item.is_favorite
        }
      });
      if (!response.success) {
        // 失败回滚
        item.is_favorite = prevValue;
        this.listError = response.error || t('ziven-dress-up.forum.equipment_history_modal.favorite_error');
        m.redraw();
      }
    } catch (error) {
      // 失败回滚
      item.is_favorite = prevValue;
      this.listError = error.message || t('ziven-dress-up.forum.equipment_history_modal.favorite_error');
      debugHelper.error('[EquipmentHistory] Toggle favorite error:', error);
      m.redraw();
    }
    this.togglingFavoriteId = null;
  }

  /**
   * 删除 (行内确认后才调)
   */
  async deleteItem(item) {
    try {
      const response = await app.request({
        method: 'DELETE',
        url: "/api/ziven/seedream/equipment-history/".concat(item.id)
      });
      if (response.success) {
        // 从本地数组移除
        this.items = this.items.filter(i => i.id !== item.id);
        this.total = Math.max(0, this.total - 1);
        this.confirmingId = null;
        m.redraw();
        debugHelper.log('[EquipmentHistory] Deleted', item.id);
      } else {
        this.listError = response.error || t('ziven-dress-up.forum.equipment_history_modal.delete_error');
        this.confirmingId = null;
        m.redraw();
      }
    } catch (error) {
      this.listError = error.message || t('ziven-dress-up.forum.equipment_history_modal.delete_error');
      this.confirmingId = null;
      debugHelper.error('[EquipmentHistory] Delete error:', error);
      m.redraw();
    }
  }

  /**
   * 选中装备 — 关闭 modal + 回调父组件
   */
  selectItem(item) {
    // v0.4.7 #364 — 复用 COS URL, **不重传**
    // 调用方拿到 { equipment_url, thumbnail_url, types }
    const data = {
      equipment_url: item.equipment_url,
      thumbnail_url: item.thumbnail_url,
      types: Array.isArray(item.types) ? [...item.types] : []
    };
    if (typeof this.attrs.onSelect === 'function') {
      this.attrs.onSelect(data);
    }
    app.modal.close();
  }

  /**
   * 格式化时间显示 ("2 分钟前" / "3 小时前" / "昨天" / "3 天前")
   *
   * 注意: Flarum DB 存的是 UTC 时间 (YYYY-MM-DD HH:MM:SS), 但 JS Date 解析
   * 不带 Z 后缀的字符串时按 local time 处理 → 偏差 8 小时 (Asia/Shanghai)
   * 必须显式 append 'Z' 强制 UTC 解析
   */
  formatTimeAgo(timestamp) {
    if (!timestamp) return '';
    // MySQL DATETIME → ISO 8601 UTC: 把空格替成 T, 加 Z 后缀强制 UTC
    const date = new Date(timestamp.replace(' ', 'T') + 'Z');
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);
    if (diffSec < 60) return t('ziven-dress-up.forum.equipment_history_modal.time_just_now');
    if (diffMin < 60) return diffMin + ' ' + t('ziven-dress-up.forum.equipment_history_modal.time_minutes_ago');
    if (diffHr < 24) return diffHr + ' ' + t('ziven-dress-up.forum.equipment_history_modal.time_hours_ago');
    if (diffDay === 1) return t('ziven-dress-up.forum.equipment_history_modal.time_yesterday');
    if (diffDay < 7) return diffDay + ' ' + t('ziven-dress-up.forum.equipment_history_modal.time_days_ago');
    if (diffDay < 30) return Math.floor(diffDay / 7) + ' ' + t('ziven-dress-up.forum.equipment_history_modal.time_weeks_ago');
    return date.toLocaleDateString('zh-CN');
  }

  /**
   * 渲染 type tag — 跟 SeedreamPage EQUIPMENT_TYPES 一致
   */
  renderTypeTags(types) {
    if (!Array.isArray(types) || types.length === 0) return null;
    return m("div", {
      class: "type-tags"
    }, types.map(typeKey => {
      const typeInfo = EQUIPMENT_TYPE_MAP[typeKey];
      if (!typeInfo) return null;
      return m("span", {
        class: "type-tag",
        key: typeKey
      }, m("i", {
        class: "fas ".concat(typeInfo.icon)
      }), typeof typeInfo.label === 'function' ? typeInfo.label() : typeInfo.label);
    }));
  }

  /**
   * v0.4.7a 返工 — view() → content()
   *
   * 根因 (辉哥 20:24 亲测 + Mavis 调研): 原 view() 覆盖了父类 Modal.view(),
   * 父 view() 在 Modal.tsx L134 返回 <div class="Modal modal-dialog fade" + className()>
   * — 包装 `.Modal-body` 的 .Modal.Modal--normal.EquipmentHistoryModal 容器就被跳过
   * 结果: className() 返回的 'EquipmentHistoryModal Modal--normal' 永远没应用到 DOM
   *       所有 .EquipmentHistoryModal xxx CSS 不命中 → 样式完全没生效
   *       ModalManager.animateShow() 找 .Modal 失败 → null.addEventListener 抛错
   *
   * 修法: 把 view() 改回 content() — 父 view() 调 our content() 拿 body 内容
   *       自动加 .Modal.Modal--normal.EquipmentHistoryModal 包装 → CSS 命中 ✓
   *       animateShow 找得到 .Modal → addEventListener 不再 null ✓
   */
  content() {
    return m("div", {
      class: "Modal-body EquipmentHistoryModal-body"
    }, this.listError && m("div", {
      class: "equipment-history-error",
      role: "alert"
    }, m("i", {
      class: "fas fa-exclamation-circle"
    }), m("span", null, this.listError), m("button", {
      class: "btn-dismiss",
      onclick: () => {
        this.listError = '';
        m.redraw();
      },
      type: "button"
    }, m("i", {
      class: "fas fa-times"
    }))), m("div", {
      class: "equipment-history-list",
      ref: el => {
        this.listRef = el;
      }
    }, this.items.length === 0 && !this.loading && m("div", {
      class: "equipment-history-empty"
    }, m("i", {
      class: "fas fa-clock-rotate-left"
    }), m("p", null, app.translator.trans('ziven-dress-up.forum.equipment_history.empty'))), this.items.map(item => {
      const isConfirming = this.confirmingId === item.id;
      return m("div", {
        key: item.id,
        class: "history-item ".concat(isConfirming ? 'confirming' : '')
      }, m("div", {
        class: "history-thumb",
        title: t('ziven-dress-up.forum.equipment_history_modal.preview_image'),
        onclick: () => {
          if (typeof app.fancyboxOpen === 'function') {
            app.fancyboxOpen(item.equipment_url);
          }
        }
      }, m("img", {
        src: item.thumbnail_url || item.equipment_url,
        alt: "equipment thumbnail",
        loading: "lazy",
        onerror: e => {
          // 缩略图加载失败 → 隐藏
          e.target.style.display = 'none';
        }
      })), m("div", {
        class: "history-meta"
      }, this.renderTypeTags(item.types), m("div", {
        class: "history-time"
      }, this.formatTimeAgo(item.last_used_at), item.use_count > 1 && m("span", {
        class: "use-count"
      }, " \xB7 \u4F7F\u7528 ", item.use_count, " \u6B21"))), isConfirming ? m("div", {
        class: "confirm-inline"
      }, m("button", {
        class: "btn-confirm btn-cancel",
        onclick: () => {
          this.confirmingId = null;
          m.redraw();
        },
        type: "button"
      }, app.translator.trans('ziven-dress-up.forum.equipment_history.cancel')), m("button", {
        class: "btn-confirm btn-danger",
        onclick: () => this.deleteItem(item),
        type: "button"
      }, app.translator.trans('ziven-dress-up.forum.equipment_history.confirm_delete'))) : m("div", {
        class: "history-actions"
      }, m("button", {
        class: "btn-use",
        onclick: () => this.selectItem(item),
        title: app.translator.trans('ziven-dress-up.forum.equipment_history.use'),
        type: "button"
      }, app.translator.trans('ziven-dress-up.forum.equipment_history.use')), m("button", {
        class: "btn-icon favorite ".concat(item.is_favorite ? 'active' : ''),
        onclick: () => this.toggleFavorite(item),
        title: item.is_favorite ? app.translator.trans('ziven-dress-up.forum.equipment_history.remove_favorite') : app.translator.trans('ziven-dress-up.forum.equipment_history.add_favorite'),
        type: "button",
        disabled: this.togglingFavoriteId === item.id
      }, m("i", {
        class: "fas fa-star"
      })), m("button", {
        class: "btn-icon delete",
        onclick: () => {
          this.confirmingId = item.id;
          m.redraw();
        },
        title: app.translator.trans('ziven-dress-up.forum.equipment_history.delete'),
        type: "button"
      }, m("i", {
        class: "fas fa-trash-alt"
      }))));
    }), this.hasMore && m("div", {
      class: "equipment-history-sentinel",
      ref: el => this.sentinelRef = el
    }, this.loading ? m("div", {
      class: "load-more"
    }, m(LoadingIndicator, {
      size: "small"
    }), m("span", null, app.translator.trans('ziven-dress-up.forum.equipment_history.loading_more'))) : m("div", {
      class: "load-more-placeholder"
    }, app.translator.trans('ziven-dress-up.forum.equipment_history.scroll_to_load'))), !this.hasMore && this.items.length > 0 && m("div", {
      class: "equipment-history-end"
    }, m("span", null, app.translator.trans('ziven-dress-up.forum.equipment_history.no_more')))));
  }
}
_defineProperty(EquipmentHistoryModal, "isDismissibleViaBackdropClick", false);
_defineProperty(EquipmentHistoryModal, "isDismissibleViaCloseButton", true);
flarum.reg.add('ziiven-dress-up', 'forum/components/EquipmentHistoryModal', EquipmentHistoryModal);